/**
 * What the address says about who is arriving (ORDR-41).
 *
 * THE CUSTOMER ADDRESS IS `/order?site=<uuid>`, and the mount `/?site=<uuid>`
 * is read the same way so a table QR code can point at the origin root. The
 * Site travels in the query, not a path segment, because `/order` is already
 * the customer mode's route in the pack and `matchShellRoute` matches segment
 * by segment: a `/order/<uuid>` would name no mode at all.
 *
 * ORDR-118 adds the BRAND address, `/order?brand=<uuid>` (and `/?brand=`): a
 * diner who knows the brand but not the location, served the location
 * chooser. Choosing is a document load to
 * `/order?brand=<b>&site=<s>&location=<l>`, the Site address plus the two
 * parameters the flow resolver already accepts.
 *
 * Every other address is a STAFF address: `/kitchen`, `/staff`, `/admin`,
 * `/onboarding`, and the bare mount with no Site. Those need a session and the
 * ordering context; the customer address needs neither.
 */

import { isStaffHost } from "../config/env.js";
import type { OrderingSurface } from "../shell/OrderingShell.js";
import { modeFromPath } from "../shell/orderingPack.js";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuidParam(search: string, name: string): string | null {
  const value = new URLSearchParams(search).get(name);
  return value !== null && UUID.test(value) ? value.toLowerCase() : null;
}

/** The Site id in the address, or `null` when it is absent or not a UUID. */
export function siteFromSearch(search: string): string | null {
  return uuidParam(search, "site");
}

/** The brand node id in the address, or `null` when it is absent or not a UUID. */
export function brandFromSearch(search: string): string | null {
  return uuidParam(search, "brand");
}

/** The location node id in the address, or `null` when it is absent or not a UUID. */
export function locationFromSearch(search: string): string | null {
  return uuidParam(search, "location");
}

/** Which surface an address opens. */
export function surfaceForAddress(pathname: string, search: string): OrderingSurface {
  const mode = modeFromPath(pathname);
  if (mode === "customer") {
    return "customer";
  }
  const params = new URLSearchParams(search);
  if (mode === null && (params.has("site") || params.has("brand"))) {
    return "customer";
  }
  return "staff";
}

/**
 * Which door an address opens, once the HOST is known too (ORDR-164).
 *
 * `host`: a brand's own domain (`harbour.pirouz.ai`) at the bare mount or
 * `/order`, with no `?site=` and no `?brand=`. The provider asks the flow read
 * which brand the host is before anything renders. On a staff host, and for
 * every address that names a Site, a brand or a staff mode, the answer is
 * `surfaceForAddress`'s, unchanged.
 */
export type Door = OrderingSurface | "host";

export function doorForAddress(
  pathname: string,
  search: string,
  hostname: string,
  staffHosts: readonly string[],
): Door {
  const surface = surfaceForAddress(pathname, search);
  if (isStaffHost(hostname, staffHosts)) {
    return surface;
  }
  const mode = modeFromPath(pathname);
  const params = new URLSearchParams(search);
  const entrance = pathname === "/" || pathname === "" || mode === "customer";
  const bare = entrance && !params.has("site") && !params.has("brand");
  return bare ? "host" : surface;
}
